import React, { useEffect, useMemo, useState } from 'react';
import TextField from '@material-ui/core/TextField';
import { GreenCheck } from './CustomCheckboxes';
import { Autocomplete } from '@material-ui/lab';
import { ISimplifiedProjectView } from '../../api-client/autogenerated';
import { projectSort } from '../../scripts/utils';
import { useSelector } from 'react-redux';
import getProjectsState from '../../features/projects/selectors';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles({
  wrap: {
    whiteSpace: 'unset',
    overflowWrap: 'break-word',
  },
  searchBarIcon: {
    marginRight: -6,
  },
  searchBarPopper: {
    minWidth: '200px !important',
    maxWidth: '300px !important',
    width: 'unset !important',
    boxShadow: '3px 5px 5px 2px rgba(0, 0, 0, 0.20)',
  },
  hideClearIcon: {
    display: 'none',
  },
});

interface Props {
  selectedProjectIds: string[];
  setSelectedProjectIds: (ids: string[]) => void;
  projectFilter?: (project: ISimplifiedProjectView) => boolean;
  style?: React.CSSProperties;
}

export default function ProjectAutocomplete(props: Props) {
  const { selectedProjectIds, setSelectedProjectIds, projectFilter = () => true, style } = props;
  const classes = useStyles();

  const projects = useSelector(getProjectsState);

  const [selectProjectInput, setSelectProjectInput] = useState<string>('');

  const handleProjectSelect = (selectedProjects: ISimplifiedProjectView[]) => {
    setSelectedProjectIds(selectedProjects.map((project) => project.id));
  };

  const selectedProjectIdsSet = useMemo(() => new Set(selectedProjectIds), [selectedProjectIds]);

  const projectOptions = useMemo(
    () =>
      projects
        .filter(projectFilter)
        .slice()
        .sort((a, b) => projectSort(selectedProjectIds, a, b)),
    [projects, selectedProjectIds, projectFilter],
  );

  const idToProject = useMemo(() => {
    const m = new Map<string, ISimplifiedProjectView>();
    for (const p of projects) m.set(p.id, p);
    return m;
  }, [projects]);

  const valueProjects = useMemo(
    () =>
      selectedProjectIds
        .map((id) => idToProject.get(id))
        .filter(Boolean) as ISimplifiedProjectView[],
    [selectedProjectIds, idToProject],
  );

  return (
    <Autocomplete
      size="small"
      multiple
      options={projectOptions}
      getOptionLabel={(project) => project.name}
      disableCloseOnSelect
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            variant="outlined"
            label={!!selectProjectInput ? '' : 'Type to search by name'}
            InputLabelProps={{
              ...params.InputLabelProps,
              shrink: false,
              style: {
                transform: 'translate(8px, 12px)',
                background: 'transparent',
                color: '#B2B1B2',
              },
            }}
            inputProps={{
              ...params.inputProps,
              style: { paddingLeft: 3 },
            }}
            InputProps={{
              ...params.InputProps,
              style: { paddingRight: 24 },
            }}
          />
        );
      }}
      renderOption={(project) => {
        const checked = selectedProjectIdsSet.has(project.id);
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <GreenCheck checked={checked} style={{ padding: 0, marginRight: 8 }} />
            <span
              className={classes.wrap}
              style={{
                fontWeight: checked ? 'bold' : undefined,
                fontStyle: project.hasOnlyCompanyAccess ? 'italic' : undefined,
                width: '100%',
              }}
            >
              {project.name}
            </span>
          </div>
        );
      }}
      renderTags={() => null}
      value={valueProjects}
      classes={{
        popupIndicator: classes.searchBarIcon,
        popper: classes.searchBarPopper,
        clearIndicator: classes.hideClearIcon,
      }}
      inputValue={selectProjectInput}
      onInputChange={(_, value) => setSelectProjectInput(value)}
      onChange={(_, value) => (value ? handleProjectSelect(value) : console.log('no value', value))}
      style={style}
    />
  );
}
